/**
 * <search-bar> — header search overlay with predictive results, recent
 * searches, and client suggested terms. Query suggestions while typing come from
 * Search & Discovery via the predictive search API.
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';

class SearchBar extends HTMLElement {
  #delegateElement = null;
  #delegateBody = null;
  #searchInputElement = null;
  #searchFormElement = null;
  #searchResultsElement = null;
  #drawerElement = null;
  #pageOverlayElement = null;
  #initialSearchLayout = null;
  #debouncedInput = null;
  #abortController = null;
  #closeListener = null;
  #boundOnKeyDown = null;
  #boundOnFormSubmit = null;
  #isOpen = false;
  #lastInputValue = '';
  #startsEmpty = false;
  #onCartDrawerOpening = () => {
    if (this.#isOpen) {
      this.#closeSearch();
    }
  };
  #onDrawerClosed = () => {
    this.#resetSearch();
    this.#detachOverlayClose();
    this.#isOpen = false;
    this.#finalizeClose();
  };

  connectedCallback() {
    if (this.#delegateElement) {
      return;
    }

    this.#delegateElement = new Delegate(this);
    this.#delegateBody = new Delegate(document.body);
    this.#searchInputElement = this.querySelector('[name="q"]');
    this.#searchFormElement = this.querySelector('.searchForm');
    this.#searchResultsElement = this.querySelector('.searchWrapper');
    this.#drawerElement = this.querySelector('#quickSearchDrawer');
    this.#pageOverlayElement = this.querySelector('.sectionOverlay');
    this.#drawerElement?.addEventListener('drawer:closed', this.#onDrawerClosed);
    this.#initialSearchLayout = this.#searchResultsElement?.cloneNode(true);
    this.#startsEmpty = this.classList.contains('search--empty');

    if (this.#searchInputElement) {
      document.documentElement.style.setProperty(
        '--search-header-height',
        `${this.#searchInputElement.offsetHeight + 32}px`
      );
    }

    if (window.theme.showRecentSearches) {
      this.#getRecentSearches();
    }

    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;

    this.#detachOverlayClose();

    if (this.#boundOnKeyDown) {
      document.removeEventListener('keydown', this.#boundOnKeyDown);
    }

    document.removeEventListener('cart-drawer:opening', this.#onCartDrawerOpening);
    this.#drawerElement?.removeEventListener('drawer:closed', this.#onDrawerClosed);

    this.#delegateElement?.off();
    this.#delegateBody?.off();
    this.#searchInputElement?.removeEventListener('input', this.#debouncedInput);
    this.#searchFormElement?.removeEventListener('submit', this.#boundOnFormSubmit);

    this.#delegateElement = null;
    this.#delegateBody = null;
    this.#debouncedInput = null;
    this.#boundOnKeyDown = null;
    this.#boundOnFormSubmit = null;
  }

  #bindEvents() {
    this.#debouncedInput = this.#debounce(this.#onInput.bind(this), 450);

    this.#delegateBody.on('click', '[data-action="open-search"]', this.#openSearch.bind(this));
    this.#delegateElement.on('click', '[data-action="close-search"]', this.#closeSearch.bind(this));
    this.#delegateElement.on('click', '[data-action="search-string"]', this.#onSuggestionActivate.bind(this));
    this.#delegateElement.on('keypress', '[data-action="search-string"]', this.#onSuggestionKeypress.bind(this));
    this.#delegateBody.on('menu:open', this.#closeSearch.bind(this));
    this.#delegateBody.on('popup:opening', this.#closeSearch.bind(this));
    this.#delegateBody.on('close:search', this.#closeSearch.bind(this));

    this.#searchInputElement?.addEventListener('input', this.#debouncedInput);

    this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
    this.#searchFormElement?.addEventListener('submit', this.#boundOnFormSubmit);

    // delegate-exempt: Escape must close overlay while input is focused
    this.#boundOnKeyDown = (event) => {
      if (event.key === 'Escape' && this.#isOpen) {
        this.#closeSearch(event);
      }
    };
    document.addEventListener('keydown', this.#boundOnKeyDown);
    document.addEventListener('cart-drawer:opening', this.#onCartDrawerOpening);
  }

  #isMobileDrawerMode() {
    return !responsive.matchesBreakpoint('lap_and_up');
  }

  #debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  #openSearch(event) {
    event.preventDefault();

    if (this.#isOpen) {
      this.#closeSearch(event);
      return;
    }

    document.dispatchEvent(
      new CustomEvent('search:opening', {
        bubbles: true,
        detail: { id: 'quickSearch' },
      })
    );

    this.style.display = 'block';
    this.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));

    this.setAttribute('aria-hidden', 'false');
    if (this.#lastInputValue === '') {
      this.classList.remove('search--results-view');
    }
    if (this.#isMobileDrawerMode()) {
      this.#drawerElement?.open?.();
    } else {
      if (this.#drawerElement) {
        this.#drawerElement.style.display = '';
        this.#drawerElement.removeAttribute('inert');
        this.#drawerElement.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('scrollLock');
    }

    this.#attachOverlayClose();

    requestAnimationFrame(() => {
      this.#searchInputElement?.focus();
    });

    this.#isOpen = true;
  }

  #attachOverlayClose() {
    if (!this.#pageOverlayElement) {
      return;
    }

    this.#pageOverlayElement.classList.add('is-visible');

    if (!this.#closeListener) {
      this.#closeListener = this.#closeSearch.bind(this);
    }

    this.#pageOverlayElement.addEventListener('click', this.#closeListener);
  }

  #detachOverlayClose() {
    if (this.#closeListener && this.#pageOverlayElement) {
      this.#pageOverlayElement.removeEventListener('click', this.#closeListener);
      this.#closeListener = null;
    }

    this.#pageOverlayElement?.classList.remove('is-visible');
  }

  #closeSearch(event) {
    event?.preventDefault();

    if (!this.#isOpen) {
      return;
    }

    if (this.#isMobileDrawerMode()) {
      this.#drawerElement?.close?.();
    } else {
      this.#resetSearch();
      this.#finalizeClose();
    }
    this.#detachOverlayClose();
    this.#isOpen = false;
  }

  #finalizeClose() {
    if (!this.#isMobileDrawerMode()) {
      document.body.classList.remove('scrollLock');
    }
    this.setAttribute('aria-hidden', 'true');
    if (this.#drawerElement) {
      this.#drawerElement.setAttribute('aria-hidden', 'true');
      this.#drawerElement.setAttribute('inert', '');
      this.#drawerElement.style.display = 'none';
    }
    this.style.display = 'none';
  }

  #onSuggestionActivate(event) {
    const item = event.target.closest('[data-action="search-string"]');
    const value = item?.dataset.value;

    if (!value || !this.#searchInputElement) {
      return;
    }

    this.#searchInputElement.value = value;
    this.#onInput({ target: this.#searchInputElement });
  }

  #onSuggestionKeypress(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.#onSuggestionActivate(event);
  }

  /**
   * The storefront search results page uses a stricter engine than predictive
   * search, so 1–2 char terms that match in the quick-search drawer return
   * nothing on /search. Appending an explicit wildcard to a short last term
   * (a → a*) makes the native results page match — pagination and filters stay
   * intact because the query still runs through Shopify's search. We navigate
   * manually with the wildcarded query so the * never shows in the visible input;
   * longer queries fall through to the native submit untouched.
   */
  #onFormSubmit(event) {
    const form = this.#searchFormElement;
    const query = this.#searchInputElement?.value.trim();

    if (!form || !query) {
      return;
    }

    const terms = query.split(/\s+/);
    const lastIndex = terms.length - 1;
    const lastTerm = terms[lastIndex];

    if (lastTerm.length >= 3 || lastTerm.endsWith('*')) {
      return;
    }

    event.preventDefault();
    terms[lastIndex] = `${lastTerm}*`;

    const params = new URLSearchParams(new FormData(form));
    params.set('q', terms.join(' '));
    window.location.assign(`${form.action}?${params}`);
  }

  async #onInput(event) {
    this.#lastInputValue = event.target.value?.trim() ?? '';
    this.#searchResultsElement = this.querySelector('.searchWrapper');

    if (!this.#searchResultsElement) {
      return;
    }

    this.#searchResultsElement.style.opacity = '0';

    if (this.#lastInputValue === '') {
      this.classList.remove('search--results-view');
      this.#resetSearch();
      return;
    }

    this.classList.remove('search--empty');
    this.classList.add('search--results-view');

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const fetchUrl = new URL(window.routes.predictive_search_url, window.location.origin);
      fetchUrl.searchParams.set('q', this.#lastInputValue);
      fetchUrl.searchParams.set('resources[type]', 'query,product');
      fetchUrl.searchParams.set('resources[limit]', '10');
      fetchUrl.searchParams.set('resources[limit_scope]', 'each');
      fetchUrl.searchParams.set('section_id', 'search-predictive');

      const response = await fetch(fetchUrl.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(String(response.status));
      }

      const text = await response.text();
      const sectionNode = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-search-predictive');

      if (!sectionNode) {
        return;
      }

      this.#searchResultsElement.innerHTML = sectionNode.innerHTML;
      this.#searchResultsElement.style.opacity = '1';

      if (window.theme.showRecentSearches) {
        this.#saveRecentSearch();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      throw error;
    }
  }

  #resetSearch() {
    if (!this.#initialSearchLayout || !this.#searchResultsElement) {
      return;
    }

    this.#abortController?.abort();
    this.#searchResultsElement.innerHTML = this.#initialSearchLayout.innerHTML;

    if (this.#searchInputElement) {
      this.#searchInputElement.value = '';
      this.#searchInputElement.removeAttribute('value');
    }

    this.#lastInputValue = '';
    this.classList.remove('search--results-view');

    if (window.theme.showRecentSearches) {
      this.#getRecentSearches();
    }

    const recentWrapper = this.querySelector('[data-search-recent]');
    const hasRecentItems = recentWrapper && !recentWrapper.classList.contains('is-hidden');
    if (this.#startsEmpty && !hasRecentItems) {
      this.classList.add('search--empty');
    } else {
      this.classList.remove('search--empty');
    }

    this.#searchResultsElement.style.opacity = '1';
  }

  #saveRecentSearch() {
    if (this.#lastInputValue.length <= 3) {
      return;
    }

    const searchTerms = JSON.parse(localStorage.getItem('searchTerms') || '[]');
    if (!searchTerms.includes(this.#lastInputValue)) {
      searchTerms.unshift(this.#lastInputValue);
    }

    try {
      localStorage.setItem('searchTerms', JSON.stringify(searchTerms.slice(0, 5)));
    } catch {
      /* ignore */
    }
  }

  #getRecentSearches() {
    const items = JSON.parse(localStorage.getItem('searchTerms') || '[]');
    const wrapper = this.querySelector('[data-search-recent]');
    const list = wrapper?.querySelector('.search--recentResults');
    const initialSuggestions = this.querySelector('[data-search-initial-suggestions]');

    if (!list || !wrapper) {
      return;
    }

    if (initialSuggestions) {
      // Suggested terms take priority over recent searches in initial view.
      wrapper.classList.add('is-hidden');
      return;
    }

    if (items.length === 0) {
      wrapper.classList.add('is-hidden');
      list.innerHTML = '';
      return;
    }

    wrapper.classList.remove('is-hidden');
    list.innerHTML = items
      .map(
        (term) =>
          `<li role="option" aria-selected="false" tabindex="0" data-action="search-string" data-value="${this.#escapeHtml(term)}"><p class="u-p3 searchSuggestion">${this.#escapeHtml(term)}</p></li>`
      )
      .join('');
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('search-bar')) {
  customElements.define('search-bar', SearchBar);
}
